// src/utils/registrationStatusUtils.js

/**
 * Verifica el estado actual de las inscripciones basado en las fechas
 * @param {string} fechaInicioInscripciones - Fecha de inicio en formato YYYY-MM-DD o ISO
 * @param {string} fechaFinInscripciones - Fecha de fin en formato YYYY-MM-DD o ISO
 * @returns {Object} Estado de las inscripciones
 */
export const getRegistrationStatus = (fechaInicioInscripciones, fechaFinInscripciones) => {
    const now = new Date();
    // Usar solo fecha local sin hora para comparación
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Función para convertir fecha string a objeto Date local (sin zona horaria)
    const parseToLocalDate = (dateString) => {
      if (!dateString) return null;
      
      // Si es formato ISO completo (con T y Z), extraer solo la fecha
      if (dateString.includes('T')) {
        const datePart = dateString.split('T')[0]; // Obtener solo "YYYY-MM-DD"
        const [year, month, day] = datePart.split('-');
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)); // Mes es 0-indexado
      }
      
      // Si es formato YYYY-MM-DD
      if (dateString.includes('-')) {
        const [year, month, day] = dateString.split('-');
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
      
      // Fallback: intentar parsear directamente
      return new Date(dateString);
    };
    
    // Convertir fechas de inscripción a objetos Date locales
    const fechaInicio = parseToLocalDate(fechaInicioInscripciones);
    const fechaFin = parseToLocalDate(fechaFinInscripciones);
    
    console.log('🔍 Debug fechas:', {
      fechaInicioInscripciones,
      fechaFinInscripciones,
      fechaInicio: fechaInicio?.toLocaleDateString(),
      fechaFin: fechaFin?.toLocaleDateString(),
      today: today.toLocaleDateString(),
      todayTime: now.toLocaleString()
    });
  
    // Si no hay fechas definidas, asumir que están abiertas
    if (!fechaInicio && !fechaFin) {
      return {
        status: 'open',
        message: null,
        canRegister: true
      };
    }
  
    // Si solo hay fecha de inicio
    if (fechaInicio && !fechaFin) {
      if (today < fechaInicio) {
        return {
          status: 'not-started',
          message: 'Las inscripciones aún no han iniciado',
          canRegister: false,
          startDate: fechaInicio
        };
      }
      return {
        status: 'open',
        message: null,
        canRegister: true
      };
    }
  
    // Si solo hay fecha de fin
    if (!fechaInicio && fechaFin) {
      if (today > fechaFin) {
        return {
          status: 'closed',
          message: 'El registro se ha cerrado',
          canRegister: false,
          endDate: fechaFin
        };
      }
      return {
        status: 'open',
        message: null,
        canRegister: true
      };
    }
  
    // Si hay ambas fechas
    if (fechaInicio && fechaFin) {
      if (today < fechaInicio) {
        return {
          status: 'not-started',
          message: 'Las inscripciones aún no han iniciado',
          canRegister: false,
          startDate: fechaInicio
        };
      }
      
      if (today > fechaFin) {
        return {
          status: 'closed',
          message: 'El registro se ha cerrado',
          canRegister: false,
          endDate: fechaFin
        };
      }
  
      return {
        status: 'open',
        message: null,
        canRegister: true
      };
    }
  
    // Fallback - asumir que están abiertas
    return {
      status: 'open',
      message: null,
      canRegister: true
    };
  };
  
  /**
   * Formatea una fecha para mostrar al usuario
   * @param {Date} date - Fecha a formatear
   * @returns {string} Fecha formateada
   */
  export const formatDateForDisplay = (date) => {
    if (!date) return '';
    
    try {
      return date.toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return date.toLocaleDateString();
    }
  };
  
  /**
   * Calcula los días restantes hasta una fecha
   * @param {Date} targetDate - Fecha objetivo
   * @returns {number} Días restantes (negativo si ya pasó)
   */
  export const getDaysUntil = (targetDate) => {
    if (!targetDate) return null;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Asegurar que targetDate también sea solo fecha sin hora
    const target = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
    
    const diffTime = target - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };